import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { eventsApi } from '../api/events';
import { Button } from '../components/Button';
import { ParGrid } from '../components/ParGrid';
import { LogoUpload } from '../components/LogoUpload';
import { ApiError } from '../api/client';
import type { HoleFlag } from '../api/types';

type Format = 'best_ball_4some' | 'stroke_play';

export function EventCreate() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [numHoles, setNumHoles] = useState(9);
  const [format, setFormat] = useState<Format>('best_ball_4some');
  const [handicapEnabled, setHandicapEnabled] = useState(false);
  const [allowGuestTeams, setAllowGuestTeams] = useState(false);
  const [pars, setPars] = useState<number[]>(Array.from({ length: 9 }, () => 4));
  const [flags, setFlags] = useState<HoleFlag[]>(
    Array.from({ length: 9 }, (_, i) => ({ holeNumber: i + 1, isKp: false, isLd: false, isHio: false })),
  );
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setPars((prev) => {
      if (prev.length === numHoles) return prev;
      if (prev.length < numHoles) return [...prev, ...Array.from({ length: numHoles - prev.length }, () => 4)];
      return prev.slice(0, numHoles);
    });
    setFlags((prev) => {
      if (prev.length === numHoles) return prev;
      if (prev.length < numHoles) {
        const extra = Array.from({ length: numHoles - prev.length }, (_, i) => ({
          holeNumber: prev.length + i + 1,
          isKp: false,
          isLd: false,
          isHio: false,
        }));
        return [...prev, ...extra];
      }
      return prev.slice(0, numHoles);
    });
  }, [numHoles]);

  const isBestBall = format === 'best_ball_4some';
  const submitFlags = useMemo(() => flags.slice(0, numHoles), [flags, numHoles]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    setSubmitting(true);
    try {
      const { event } = await eventsApi.create({
        name: name.trim(),
        numHoles,
        format,
        handicapEnabled,
        pars,
        logoUrl,
        allowGuestTeams: isBestBall ? allowGuestTeams : false,
        holeFlags: submitFlags,
      });
      navigate(`/e/${event.id}`);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not create event');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container">
      <form onSubmit={onSubmit} className="card stack">
        <h1>Create event</h1>
        {err && <div className="error">{err}</div>}

        <div>
          <label htmlFor="name">Event name</label>
          <input id="name" required minLength={1} maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />
        </div>

        <div>
          <label>Logo (optional)</label>
          <LogoUpload value={logoUrl} onChange={setLogoUrl} />
        </div>

        <div className="row" style={{ gap: 16 }}>
          <div style={{ flex: 1 }}>
            <label htmlFor="holes">Number of holes</label>
            <select id="holes" value={numHoles} onChange={(e) => setNumHoles(Number(e.target.value))}>
              {Array.from({ length: 10 }, (_, i) => i + 9).map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label htmlFor="format">Format</label>
            <select id="format" value={format} onChange={(e) => setFormat(e.target.value as Format)}>
              <option value="best_ball_4some">Best Ball 4some</option>
              <option value="stroke_play">Stroke Play</option>
            </select>
          </div>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400 }}>
          <input type="checkbox" checked={handicapEnabled} onChange={(e) => setHandicapEnabled(e.target.checked)} />
          Enable handicap (show Net score)
        </label>

        {isBestBall && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400 }}>
            <input
              type="checkbox"
              checked={allowGuestTeams}
              onChange={(e) => setAllowGuestTeams(e.target.checked)}
            />
            Allow guests to create teams
          </label>
        )}

        <div>
          <label>Par, KP, LD and HIO per hole</label>
          <p className="muted" style={{ fontSize: 12, margin: '0 0 8px' }}>
            Tap KP for Closest-to-Pin, LD for Longest Drive, HIO for the single Hole-in-One prize hole. Only one HIO hole per event; KP/LD/HIO are mutually exclusive per hole.
          </p>
          <ParGrid pars={pars} flags={flags} onChange={setPars} onFlagsChange={setFlags} showFlags />
        </div>

        <Button type="submit" block disabled={submitting}>{submitting ? 'Saving…' : 'Create event'}</Button>
      </form>
    </div>
  );
}
